import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Curriculum from "@/pages/Curriculum";
import CommandReference from "@/pages/CommandReference";
import Certificates from "@/pages/Certificates";
import AdminDashboard from "@/pages/AdminDashboard";
import TempPasswords from "@/pages/TempPasswords";
import AdminAchievements from "@/pages/AdminAchievements";
import AdminAntiPaste from "@/pages/AdminAntiPaste";
import Leaderboard from "@/pages/Leaderboard";
import AdminLeaderboard from "@/pages/AdminLeaderboard";
import Instructor from "@/pages/Instructor";
import SetupAccount from "@/pages/SetupAccount";
import ChangePassword from "@/pages/ChangePassword";
import LabWorkspace from "@/pages/LabWorkspace";
import Quizzes from "@/pages/Quizzes";
import QuizTake from "@/pages/QuizTake";
import Az900 from "@/pages/Az900";
import GuidedLabs from "@/pages/GuidedLabs";
import PracticalExam from "@/pages/PracticalExam";
import CertificateVerify from "@/pages/CertificateVerify";
import VirtualLecturer from "@/pages/VirtualLecturer";
import AzurePortal from "@/pages/AzurePortal";
import AwsConsole from "@/pages/AwsConsole";
import LabPortal from "@/pages/LabPortal";
import SqlLab from "@/pages/SqlLab";
import WindowsLab from "@/pages/WindowsLab";
import AdminLabs from "@/pages/AdminLabs";
import NotFound from "@/pages/NotFound";
import LabChrome from "@/components/LabChrome";
import { AzureProvider } from "@/context/AzureContext";
import AppLayout from "@/components/AppLayout";
import GuidedPopout from "@/pages/GuidedPopout";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import Onboarding from "@/pages/Onboarding";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AccountBilling from "@/pages/AccountBilling";
import AdminPricing from "@/pages/AdminPricing";
import InterviewPrepHub from "@/pages/InterviewPrepHub";
import GrcLab from "@/pages/GrcLab";
import SocLab from "@/pages/SocLab";
import AnalystLab from "@/pages/AnalystLab";
import { LabGate } from "@/components/LabGate";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="h-screen w-full grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/setup-account" element={<SetupAccount />} />
            <Route
              path="/change-password"
              element={
                <Protected>
                  <ChangePassword />
                </Protected>
              }
            />
            <Route
              path="/lab/:labId"
              element={
                <Protected>
                  <LabWorkspace />
                </Protected>
              }
            />
            <Route
              path="/quiz/:quizId"
              element={
                <Protected>
                  <QuizTake />
                </Protected>
              }
            />
            <Route
              path="/azure/guided"
              element={
                <Protected>
                  <LabGate product="azure" name="Azure Lab">
                    <AzureProvider>
                      <GuidedPopout />
                    </AzureProvider>
                  </LabGate>
                </Protected>
              }
            />
            <Route
              path="/azure"
              element={
                <Protected>
                  <LabGate product="azure" name="Azure Lab">
                    <AzureProvider>
                      <AzurePortal />
                    </AzureProvider>
                  </LabGate>
                </Protected>
              }
            />
            <Route
              path="/aws-console"
              element={
                <Protected>
                  <LabGate product="aws" name="AWS Lab">
                    <AwsConsole />
                  </LabGate>
                </Protected>
              }
            />
            <Route
              path="/labs"
              element={
                <Protected>
                  <LabPortal />
                </Protected>
              }
            />
            <Route
              path="/sql-lab"
              element={
                <Protected>
                  <LabGate product="sql" name="SQL Server Lab">
                    <SqlLab />
                  </LabGate>
                </Protected>
              }
            />
            <Route
              path="/windows-lab"
              element={
                <Protected>
                  <LabGate product="windows" name="Windows Server Lab">
                    <WindowsLab />
                  </LabGate>
                </Protected>
              }
            />
            <Route
              path="/interview-prep"
              element={
                <Protected>
                  <LabChrome title="Interview Prep Lab" subtitle="Career interview preparation" accent="#0ea5a3" contentClassName="overflow-y-auto">
                    <InterviewPrepHub />
                  </LabChrome>
                </Protected>
              }
            />
            <Route
              path="/interview-prep/grc"
              element={
                <Protected>
                  <GrcLab />
                </Protected>
              }
            />
            <Route
              path="/interview-prep/soc"
              element={
                <Protected>
                  <SocLab />
                </Protected>
              }
            />
            <Route
              path="/interview-prep/iam"
              element={
                <Protected>
                  <AnalystLab apiBase="iam" />
                </Protected>
              }
            />
            <Route
              path="/interview-prep/cloud"
              element={
                <Protected>
                  <AnalystLab apiBase="cloud" />
                </Protected>
              }
            />
            <Route
              path="/interview-prep/sysadmin"
              element={
                <Protected>
                  <AnalystLab apiBase="sysadmin" />
                </Protected>
              }
            />
            <Route
              path="/interview-prep/itsupport"
              element={
                <Protected>
                  <AnalystLab apiBase="itsupport" />
                </Protected>
              }
            />
            <Route
              path="/interview-prep/cloudapp"
              element={
                <Protected>
                  <AnalystLab apiBase="cloudapp" />
                </Protected>
              }
            />
            <Route
              path="/interview-prep/dba"
              element={
                <Protected>
                  <AnalystLab apiBase="dba" />
                </Protected>
              }
            />
            <Route
              path="/interview-prep/m365"
              element={
                <Protected>
                  <AnalystLab apiBase="m365" />
                </Protected>
              }
            />
            <Route
              path="/interview-prep/azureeng"
              element={
                <Protected>
                  <AnalystLab apiBase="azureeng" />
                </Protected>
              }
            />
            <Route
              path="/interview-prep/aieng"
              element={
                <Protected>
                  <AnalystLab apiBase="aieng" />
                </Protected>
              }
            />
            <Route
              path="/interview-prep/devops"
              element={
                <Protected>
                  <AnalystLab apiBase="devops" />
                </Protected>
              }
            />
            <Route
              path="/interview-prep/servicenow"
              element={
                <Protected>
                  <AnalystLab apiBase="servicenow" />
                </Protected>
              }
            />
            <Route
              path="/interview-prep/aiconsult"
              element={
                <Protected>
                  <AnalystLab apiBase="aiconsult" />
                </Protected>
              }
            />
            <Route
              path="/az900"
              element={
                <Protected>
                  <LabChrome title="Azure Lab · AZ-900 Exam" subtitle="Microsoft Azure Fundamentals" accent="#0078d4" contentClassName="overflow-y-auto">
                    <Az900 />
                  </LabChrome>
                </Protected>
              }
            />
            <Route
              path="/guided"
              element={
                <Protected>
                  <LabChrome title="Azure Lab · Cloud Engineer Labs" subtitle="Guided, state-validated scenarios" accent="#0078d4" contentClassName="overflow-y-auto">
                    <GuidedLabs />
                  </LabChrome>
                </Protected>
              }
            />
            <Route
              path="/exam"
              element={
                <Protected>
                  <AzureProvider>
                    <LabChrome title="Azure Lab · Practical Exam" subtitle="AZ-104 blank-tenant capstone" accent="#0078d4" contentClassName="overflow-y-auto">
                      <PracticalExam />
                    </LabChrome>
                  </AzureProvider>
                </Protected>
              }
            />
            <Route path="/verify/:certId" element={<CertificateVerify />} />
            <Route
              element={
                <Protected>
                  <AppLayout />
                </Protected>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/billing/success" element={<BillingSuccess />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/account/billing" element={<AccountBilling />} />
              <Route path="/admin/pricing" element={<AdminPricing />} />
              <Route path="/curriculum" element={<Curriculum />} />
              <Route path="/quizzes" element={<Quizzes />} />
              <Route path="/commands" element={<CommandReference />} />
              <Route path="/certificates" element={<Certificates />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/instructor" element={<Instructor />} />
              <Route path="/admin/lecturer" element={<VirtualLecturer />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/labs" element={<AdminLabs />} />
              <Route path="/admin/temp-passwords" element={<TempPasswords />} />
              <Route path="/admin/achievements" element={<AdminAchievements />} />
              <Route path="/admin/anti-paste" element={<AdminAntiPaste />} />
              <Route path="/admin/leaderboard" element={<AdminLeaderboard />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
